<template>
  <v-row class="import-job-detail">
    <v-col cols="12">
      <div class="d-flex align-center mb-4">
        <v-btn icon class="mr-2" @click="goBack">
          <v-icon>{{ icons.back }}</v-icon>
        </v-btn>
        <h2 class="text-h5 font-weight-medium">Import Job Details</h2>
      </div>
    </v-col>

    <!-- Loading State -->
    <v-col cols="12" v-if="loading && !job">
      <v-card outlined class="pa-8 text-center">
        <v-progress-circular indeterminate color="primary" size="48" />
        <p class="mt-4 grey--text">Loading job details...</p>
      </v-card>
    </v-col>

    <!-- Job Details -->
    <template v-if="job">
      <v-col cols="12">
        <v-card outlined class="pa-4">
          <div class="d-flex justify-space-between align-center">
            <v-card-title class="px-0 pt-0">Job Information</v-card-title>
            <div v-if="canRetry">
              <v-btn
                small
                color="warning"
                class="mr-2"
                :loading="retryingIncomplete"
                :disabled="
                  retryingIncomplete ||
                    retryingAll ||
                    incompleteSubJobsCount === 0
                "
                @click="retryIncomplete"
              >
                <v-icon small class="mr-1">{{ icons.retry }}</v-icon>
                Retry Incomplete ({{ incompleteSubJobsCount }})
              </v-btn>
              <v-btn
                small
                color="primary"
                :loading="retryingAll"
                :disabled="retryingIncomplete || retryingAll"
                @click="retryAll"
              >
                <v-icon small class="mr-1">{{ icons.refresh }}</v-icon>
                Retry All
              </v-btn>
            </div>
          </div>
          <v-divider class="mb-4" />

          <v-row>
            <v-col cols="12" md="6">
              <div class="d-flex mb-3">
                <span class="grey--text text--darken-1" style="width: 140px"
                  >Status:</span
                >
                <v-chip :color="getStatusColor(job.status)" small dark>
                  {{ capitalize(job.status) }}
                </v-chip>
              </div>
              <div class="d-flex mb-3">
                <span class="grey--text text--darken-1" style="width: 140px"
                  >Google Sheet ID:</span
                >
                <a
                  :href="
                    `https://docs.google.com/spreadsheets/d/${job.source_google_sheet_id}`
                  "
                  target="_blank"
                  class="text-decoration-none"
                >
                  {{ job.source_google_sheet_id }}
                </a>
              </div>
              <div class="d-flex mb-3">
                <span class="grey--text text--darken-1" style="width: 140px"
                  >Created At:</span
                >
                <span>{{ formatDate(job.created_at) }}</span>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="d-flex mb-3">
                <span class="grey--text text--darken-1" style="width: 140px"
                  >Total Rows:</span
                >
                <strong>{{ job.total_rows }}</strong>
              </div>
              <div class="d-flex mb-3">
                <span class="grey--text text--darken-1" style="width: 140px"
                  >Success Count:</span
                >
                <strong class="success--text">{{ job.success_count }}</strong>
              </div>
              <div class="d-flex mb-3">
                <span class="grey--text text--darken-1" style="width: 140px"
                  >Pending Count:</span
                >
                <strong class="warning--text">{{
                  getPendingCount(job)
                }}</strong>
              </div>
              <div class="d-flex mb-3">
                <span class="grey--text text--darken-1" style="width: 140px"
                  >Completed At:</span
                >
                <span>{{
                  job.completed_at ? formatDate(job.completed_at) : '-'
                }}</span>
              </div>
            </v-col>
          </v-row>

          <!-- Config Options -->
          <div v-if="hasConfigOptions" class="mt-4">
            <span class="grey--text text--darken-1 d-block mb-2"
              >Post-Import Options:</span
            >
            <v-chip
              v-if="job.config && job.config.clear_new_meal_guides_category"
              small
              class="mr-2"
              color="orange"
              dark
            >
              Clear 'New Meal Guides' Category
            </v-chip>
            <v-chip
              v-if="job.config && job.config.add_to_new_meal_guides_category"
              small
              color="blue"
              dark
            >
              Add to 'New Meal Guides' Category
            </v-chip>
          </div>

          <!-- Progress Bar -->
          <div v-if="job.total_rows" class="mt-4">
            <span class="grey--text text--darken-1 d-block mb-2"
              >Progress:</span
            >
            <v-progress-linear
              :value="getProgressPercentage(job)"
              :color="getProgressColor(job)"
              height="24"
              rounded
            >
              <template v-slot:default>
                <span class="white--text font-weight-medium">
                  {{ job.success_count }} / {{ job.total_rows }} ({{
                    Math.round(getProgressPercentage(job))
                  }}%)
                </span>
              </template>
            </v-progress-linear>
          </div>

          <!-- Logs -->
          <div v-if="job.logs" class="mt-4">
            <span class="grey--text text--darken-1 d-block mb-2">Logs:</span>
            <v-card outlined class="pa-3 logs-container" color="grey lighten-4">
              <pre class="text-body-2 mb-0" style="white-space: pre-wrap">{{
                job.logs
              }}</pre>
            </v-card>
          </div>
        </v-card>
      </v-col>

      <!-- Sub Jobs -->
      <v-col cols="12">
        <v-card outlined class="pa-4">
          <v-card-title class="px-0 pt-0">
            Sub Jobs ({{ subJobs.length }})
          </v-card-title>
          <v-divider class="mb-4" />

          <v-simple-table>
            <thead>
              <tr>
                <th>Sheet Name</th>
                <th>Google Sheet ID</th>
                <th>Status</th>
                <th>Total Rows</th>
                <th>Started At</th>
                <th>Completed At</th>
                <th>Logs</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="subJobs.length === 0">
                <td colspan="7" class="text-center grey--text py-8">
                  No sub jobs found
                </td>
              </tr>

              <tr v-for="subJob in subJobs" :key="subJob.id">
                <td>
                  <strong>{{ subJob.google_sheet_name || '-' }}</strong>
                </td>
                <td>
                  <a
                    :href="
                      `https://docs.google.com/spreadsheets/d/${subJob.google_sheet_id}`
                    "
                    target="_blank"
                    class="text-decoration-none"
                    style="
                      max-width: 200px;
                      overflow: hidden;
                      text-overflow: ellipsis;
                      white-space: nowrap;
                      display: inline-block;
                    "
                  >
                    {{ subJob.google_sheet_id }}
                  </a>
                </td>
                <td>
                  <v-chip :color="getStatusColor(subJob.status)" small dark>
                    {{ capitalize(subJob.status) }}
                  </v-chip>
                </td>
                <td>{{ subJob.total_rows }}</td>
                <td>
                  {{ subJob.started_at ? formatDate(subJob.started_at) : '-' }}
                </td>
                <td>
                  {{
                    subJob.completed_at ? formatDate(subJob.completed_at) : '-'
                  }}
                </td>
                <td>
                  <v-tooltip v-if="subJob.logs" bottom max-width="400">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        @click="showSubJobLogs(subJob)"
                      >
                        <v-icon small color="info">{{ icons.info }}</v-icon>
                      </v-btn>
                    </template>
                    <span>Click to view logs</span>
                  </v-tooltip>
                  <span v-else>-</span>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>
      </v-col>
    </template>

    <!-- Logs Dialog -->
    <v-dialog v-model="logsDialog" max-width="600">
      <v-card>
        <v-card-title>
          Sub Job Logs
          <v-spacer />
          <v-btn icon @click="logsDialog = false">
            <v-icon>{{ icons.close }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <pre
            v-if="selectedSubJobLogs"
            class="text-body-2"
            style="white-space: pre-wrap"
            >{{ selectedSubJobLogs }}</pre
          >
          <span v-else class="grey--text">No logs available</span>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Toast Notification -->
    <toast ref="toast" />
  </v-row>
</template>

<script>
import {
  mdiArrowLeft,
  mdiInformationOutline,
  mdiClose,
  mdiRefresh,
  mdiReplay,
} from '@mdi/js'
import moment from 'moment'
import Toast from '@/components/elements/Toast'

export default {
  name: 'ImportJobDetailPage',

  components: {
    Toast,
  },

  data() {
    return {
      loading: false,
      job: null,
      logsDialog: false,
      selectedSubJobLogs: null,
      autoRefreshTimer: null,
      retryingAll: false,
      retryingIncomplete: false,
      icons: {
        back: mdiArrowLeft,
        info: mdiInformationOutline,
        close: mdiClose,
        refresh: mdiRefresh,
        retry: mdiReplay,
      },
    }
  },

  computed: {
    jobId() {
      return this.$route.params.id
    },

    subJobs() {
      return this.job?.sub_jobs || []
    },

    incompleteSubJobsCount() {
      return this.subJobs.filter((s) => s.status !== 'completed').length
    },

    canRetry() {
      return this.job && !['pending', 'processing'].includes(this.job.status)
    },

    hasConfigOptions() {
      if (!this.job || !this.job.config) return false
      return (
        this.job.config.clear_new_meal_guides_category ||
        this.job.config.add_to_new_meal_guides_category
      )
    },
  },

  created() {
    this.fetchJobDetail()
  },

  beforeDestroy() {
    this.stopAutoRefresh()
  },

  methods: {
    goBack() {
      this.$router.push({ name: 'import.meals' })
    },

    async fetchJobDetail() {
      if (!this.jobId) {
        this.$refs.toast?.open({
          color: 'error',
          message: 'Job ID is required',
        })
        return
      }

      this.loading = true
      try {
        const { data } = await this.$api.get(
          `meals/guides/import/${this.jobId}`
        )
        this.job = data.data || data

        // Start or stop auto-refresh based on job status
        if (this.shouldAutoRefresh()) {
          this.startAutoRefresh()
        } else {
          this.stopAutoRefresh()
        }
      } catch (error) {
        this.$refs.toast?.open({
          color: 'error',
          message:
            error.response?.data?.message || 'Failed to fetch job details',
        })
      } finally {
        this.loading = false
      }
    },

    shouldAutoRefresh() {
      return this.job && ['pending', 'processing'].includes(this.job.status)
    },

    startAutoRefresh() {
      if (this.autoRefreshTimer) return
      this.autoRefreshTimer = setInterval(() => {
        this.refreshJobStatus()
      }, 3000)
    },

    stopAutoRefresh() {
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer)
        this.autoRefreshTimer = null
      }
    },

    async refreshJobStatus() {
      try {
        const { data } = await this.$api.get(
          `meals/guides/import/${this.jobId}`
        )
        this.job = data.data || data

        // Stop auto-refresh if job is no longer pending or processing
        if (!this.shouldAutoRefresh()) {
          this.stopAutoRefresh()
        }
      } catch (error) {
        // Silently fail on auto-refresh errors
      }
    },

    capitalize(str) {
      if (!str) return ''
      return str.charAt(0).toUpperCase() + str.slice(1)
    },

    getStatusColor(status) {
      const colors = {
        pending: 'orange',
        processing: 'blue',
        completed: 'green',
        failed: 'red',
        cancelled: 'grey',
      }
      return colors[status] || 'grey'
    },

    getPendingCount(job) {
      if (!job.total_rows) return 0
      return job.total_rows - job.success_count
    },

    getProgressPercentage(job) {
      if (!job.total_rows) return 0
      return (job.success_count / job.total_rows) * 100
    },

    getProgressColor(job) {
      if (job.status === 'completed') {
        return 'success'
      }
      if (job.status === 'failed') {
        return 'error'
      }
      return 'primary'
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return moment(dateString).format('MMM DD, YYYY HH:mm')
    },

    showSubJobLogs(subJob) {
      this.selectedSubJobLogs = subJob.logs
      this.logsDialog = true
    },

    async retryAll() {
      this.retryingAll = true
      try {
        await this.$api.post(`meals/guides/import/${this.jobId}/rerun`, {
          rerun_mode: 'all',
        })
        this.$refs.toast?.open({
          color: 'success',
          message: 'Retry all initiated successfully',
        })
        // Refresh job details
        await this.fetchJobDetail()
      } catch (error) {
        this.$refs.toast?.open({
          color: 'error',
          message:
            error.response?.data?.message || 'Failed to retry all sub jobs',
        })
      } finally {
        this.retryingAll = false
      }
    },

    async retryIncomplete() {
      this.retryingIncomplete = true
      try {
        await this.$api.post(`meals/guides/import/${this.jobId}/rerun`, {
          rerun_mode: 'incomplete',
        })
        this.$refs.toast?.open({
          color: 'success',
          message: 'Retry incomplete initiated successfully',
        })
        // Refresh job details
        await this.fetchJobDetail()
      } catch (error) {
        this.$refs.toast?.open({
          color: 'error',
          message:
            error.response?.data?.message ||
            'Failed to retry incomplete sub jobs',
        })
      } finally {
        this.retryingIncomplete = false
      }
    },
  },
}
</script>

<style scoped>
.import-job-detail h2 {
  font-size: 1.5rem;
  font-weight: 500;
}

pre {
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
}

.logs-container {
  max-height: 200px;
  overflow-y: auto;
}
</style>
